/**
 * iter212b — Duplicate active-job reconciliation modal.
 *
 * Shown when POST /commission-workflows returns HTTP 409 with
 * detail.code === "duplicate_active_job". Presents the FOH with three
 * clean choices instead of a raw error toast:
 *
 *   Open Existing (primary)
 *   Cancel Existing (secondary, destructive, requires confirmation)
 *   Go Back
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, ArrowRight, XCircle, ChevronLeft, Loader2,
  Car, Wrench, User, Clock, FileText,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKFLOW_LABELS = {
  mot:                  "MOT",
  vehicle_inspection:   "Vehicle Inspection",
  diagnostic:           "Diagnostic",
  service:              "Service Workflow",
};

function formatWorkflows(keys) {
  if (!Array.isArray(keys) || keys.length === 0) return "workflows";
  return keys.map((k) => WORKFLOW_LABELS[k] || k).join(", ");
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

export default function DuplicateJobReconcileModal({
  detail,          // full detail payload from the 409
  onGoBack,        // called when the user hits "Go Back"
  onResolved,      // called after Cancel succeeds so caller can retry commissioning
}) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason]         = useState("");
  const [busy, setBusy]             = useState(false);

  if (!detail || detail.code !== "duplicate_active_job") return null;

  const existingJobId = detail.existing_job_id;

  const handleOpenExisting = () => {
    if (!existingJobId) {
      toast.error("Missing existing job id — cannot navigate.");
      return;
    }
    // Route to whichever surface makes sense based on the existing
    // job's context. Default to the FOH job workspace.
    navigate(`/app/foh/job/${existingJobId}`);
  };

  const handleCancelStart = () => setConfirming(true);
  const handleCancelAbort = () => { setConfirming(false); setReason(""); };

  const handleCancelConfirm = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toast.error("Please enter a short reason (min 3 characters).");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/foh/jobs/${existingJobId}/cancel`,
        { reason: trimmed },
      );
      toast.success("Existing job cancelled. You can now commission the workflows.");
      setBusy(false);
      onResolved?.();
    } catch (e) {
      setBusy(false);
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
                : d?.message ? d.message
                : "Couldn't cancel the existing job.";
      toast.error(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="dup-job-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-amber-800">
              Duplicate active job
            </p>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5">
              This vehicle already has an active Yestimate job
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              You asked to commission {formatWorkflows(detail.requested_workflows)}
              {" "}on <span className="font-mono">{fmt(detail.reg)}</span>
              {detail.gh_jobsheet_number && (
                <> for GH jobsheet <span className="font-mono">{detail.gh_jobsheet_number}</span></>
              )}.
            </p>
          </div>
        </div>

        {/* Existing job summary */}
        <div className="px-6 py-4 border-b border-slate-200 space-y-2 text-sm">
          <Row icon={FileText} label="Existing job"
               value={<span className="font-mono">{fmt(existingJobId)}</span>}
               testId="dup-existing-job-id" />
          <Row icon={Car}     label="Registration"
               value={<span className="font-mono">{fmt(detail.reg)}</span>} />
          <Row icon={Wrench}  label="Workflow"
               value={fmt(detail.existing_inspection_type_label
                          || (detail.existing_services_requested || []).join(", "))} />
          <Row icon={Clock}   label="Created" value={fmtDate(detail.existing_created_at)} />
          <Row icon={User}    label="Status"
               value={fmt(detail.existing_inspection_status || detail.existing_status)} />
          {detail.existing_technician && (
            <Row icon={User}  label="Technician" value={fmt(detail.existing_technician)} />
          )}
          {detail.existing_advisor && (
            <Row icon={User}  label="Advisor" value={fmt(detail.existing_advisor)} />
          )}
        </div>

        {/* Body */}
        {!confirming ? (
          <div className="px-6 py-5 space-y-3">
            <p className="text-sm text-slate-700">
              We recommend <span className="font-semibold">opening the existing job</span>{" "}
              rather than creating another one. Cancelling should be a last resort
              — it will detach the workflow from the current technician.
            </p>
            <div className="flex flex-col gap-2 pt-1">
              <Button
                onClick={handleOpenExisting}
                className="bg-teal-700 hover:bg-teal-800 text-white justify-between"
                data-testid="dup-open-existing-btn"
              >
                Open Existing Job
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelStart}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 justify-between"
                data-testid="dup-cancel-existing-btn"
              >
                Cancel Existing Job
                <XCircle className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onGoBack}
                className="text-slate-600 justify-between"
                data-testid="dup-go-back-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Go Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3 bg-red-50/40">
            <p className="text-sm text-slate-800 font-semibold">
              Cancel the existing job?
            </p>
            <p className="text-xs text-slate-600 leading-relaxed">
              This will mark job <span className="font-mono">{existingJobId}</span> as
              cancelled and detach it from the intake session. A fresh job can then be
              created when you re-commission the workflows. This action is audited.
            </p>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mt-1">
              Reason for cancellation
            </label>
            <textarea
              rows={2}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Superseded by rebooked jobsheet SJ027001"
              data-testid="dup-cancel-reason-input"
              disabled={busy}
              maxLength={500}
            />
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                onClick={handleCancelAbort}
                disabled={busy}
                data-testid="dup-cancel-nevermind-btn"
              >
                Never mind
              </Button>
              <Button
                onClick={handleCancelConfirm}
                disabled={busy}
                className="bg-red-600 hover:bg-red-700 text-white flex-1"
                data-testid="dup-cancel-confirm-btn"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm cancellation
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 w-24 shrink-0">
        {label}
      </span>
      <span className="text-sm text-slate-800 truncate">{value}</span>
    </div>
  );
}
